import { DEFAULT_RANGE_BAR_OPTION } from '@/enums/range-bar-options';
import { DEFAULT_RANGE_GAP_OPTION } from '@/enums/range-gap-options';
import { RangeDaysOption } from '@/enums/range-days-options';
import { RangeMonthsOption } from '@/enums/range-months-options';
import { RangeYearsOption, isRangeYearsOptionAllowedForFreeUsers, isRangeYearsOptionAllowedForBasicMembers, isRangeYearsOptionAllowedForExtraMembers } from '@/enums/range-years-options';

export interface RangeOption {
  title: string;
  shortTitle: string;
  useBarAndGap: boolean;
  useNumberOfDays: boolean;
  useStartDateAndEndDate: boolean;
  numberOfDays: RangeDaysOption;
  numberOfMonths: RangeMonthsOption;
  numberOfYears: RangeYearsOption;
  barValue: string;
  gapValue: string;
  isAllowedForFreeUsers: boolean;
  isAllowedForBasicMembers: boolean;
  isAllowedForExtraMembers: boolean;
  dividerAbove: boolean;
  isSimpleRangeSetting: boolean;
};

export const RANGE_OPTION_FILL_THE_CHART: RangeOption = {
  title: 'Fill the Chart',
  shortTitle: 'Fill Chart',
  useBarAndGap: true,
  useNumberOfDays: false,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.One),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.One),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.One),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_ONE_DAY: RangeOption = {
  title: '1 Day',
  shortTitle: '1 Day',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.One,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: true,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TWO_DAYS: RangeOption = {
  title: '2 Days',
  shortTitle: '2 Days',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Two,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_FIVE_DAYS: RangeOption = {
  title: '5 Days',
  shortTitle: '5 Days',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Five,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TEN_DAYS: RangeOption = {
  title: '10 Days',
  shortTitle: '10 Days',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Ten,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_FIFTEEN_DAYS: RangeOption = {
  title: '15 Days',
  shortTitle: '15 Days',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Fifteen,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_ONE_MONTH: RangeOption = {
  title: '1 Month',
  shortTitle: '1 Month',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.One,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_THREE_MONTHS: RangeOption = {
  title: '3 Months',
  shortTitle: '3 Months',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Three,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_SIX_MONTHS: RangeOption = {
  title: '6 Months',
  shortTitle: '6 Months',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Six,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_NINE_MONTHS: RangeOption = {
  title: '9 Months',
  shortTitle: '9 Months',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Nine,
  numberOfYears: RangeYearsOption.Zero,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Zero),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Zero),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Zero),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_YEAR_TO_DATE: RangeOption = {
  title: 'Year-to-Date',
  shortTitle: 'YTD',
  useBarAndGap: false,
  useNumberOfDays: false,
  useStartDateAndEndDate: true,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: true,
  isAllowedForBasicMembers: true,
  isAllowedForExtraMembers: true,
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_ONE_YEAR: RangeOption = {
  title: '1 Year',
  shortTitle: '1 Year',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.One),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.One),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.One),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_ONE_AND_A_HALF_YEARS: RangeOption = {
  title: '1.5 Years',
  shortTitle: '1.5 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Six,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.One),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.One),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.One),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TWO_YEARS: RangeOption = {
  title: '2 Years',
  shortTitle: '2 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Two,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Two),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Two),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Two),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_THREE_YEARS: RangeOption = {
  title: '3 Years',
  shortTitle: '3 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Three,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Three),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Three),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Three),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_FIVE_YEARS: RangeOption = {
  title: '5 Years',
  shortTitle: '5 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Five,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Five),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Five),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Five),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TEN_YEARS: RangeOption = {
  title: '10 Years',
  shortTitle: '10 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Ten,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Ten),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Ten),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Ten),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_FIFTEEN_YEARS: RangeOption = {
  title: '15 Years',
  shortTitle: '15 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Fifteen,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Fifteen),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Fifteen),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Fifteen),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TWENTY_YEARS: RangeOption = {
  title: '20 Years',
  shortTitle: '20 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Twenty,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Twenty),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Twenty),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Twenty),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_TWENTY_FIVE_YEARS: RangeOption = {
  title: '25 Years',
  shortTitle: '25 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.TwentyFive,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.TwentyFive),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.TwentyFive),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.TwentyFive),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_FIFTY_YEARS: RangeOption = {
  title: '50 Years',
  shortTitle: '50 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.Fifty,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.Fifty),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.Fifty),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.Fifty),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_SEVENTY_FIVE_YEARS: RangeOption = {
  title: '75 Years',
  shortTitle: '75 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.SeventyFive,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.SeventyFive),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.SeventyFive),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.SeventyFive),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_ONE_HUNDRED_YEARS: RangeOption = {
  title: '100 Years',
  shortTitle: '100 Years',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.OneHundred,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: isRangeYearsOptionAllowedForFreeUsers(RangeYearsOption.OneHundred),
  isAllowedForBasicMembers: isRangeYearsOptionAllowedForBasicMembers(RangeYearsOption.OneHundred),
  isAllowedForExtraMembers: isRangeYearsOptionAllowedForExtraMembers(RangeYearsOption.OneHundred),
  dividerAbove: false,
  isSimpleRangeSetting: true
};

export const RANGE_OPTION_PREDEFINED_RANGE: RangeOption = {
  title: 'Predefined Range',
  shortTitle: 'Predefined',
  useBarAndGap: false,
  useNumberOfDays: true,
  useStartDateAndEndDate: false,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: true,
  isAllowedForBasicMembers: true,
  isAllowedForExtraMembers: true,
  dividerAbove: true,
  isSimpleRangeSetting: false
};

export const RANGE_OPTION_SELECT_START_END: RangeOption = {
  title: 'Select Start/End',
  shortTitle: 'Custom',
  useBarAndGap: false,
  useNumberOfDays: false,
  useStartDateAndEndDate: true,
  numberOfDays: RangeDaysOption.Zero,
  numberOfMonths: RangeMonthsOption.Zero,
  numberOfYears: RangeYearsOption.One,
  barValue: DEFAULT_RANGE_BAR_OPTION,
  gapValue: DEFAULT_RANGE_GAP_OPTION,
  isAllowedForFreeUsers: true,
  isAllowedForBasicMembers: true,
  isAllowedForExtraMembers: true,
  dividerAbove: false,
  isSimpleRangeSetting: false
};

export const DEFAULT_RANGE_OPTION: RangeOption = RANGE_OPTION_ONE_YEAR;

export const RANGE_OPTIONS_ARRAY = [
  RANGE_OPTION_FILL_THE_CHART,
  RANGE_OPTION_ONE_DAY,
  RANGE_OPTION_TWO_DAYS,
  RANGE_OPTION_FIVE_DAYS,
  RANGE_OPTION_TEN_DAYS,
  RANGE_OPTION_FIFTEEN_DAYS,
  RANGE_OPTION_ONE_MONTH,
  RANGE_OPTION_THREE_MONTHS,
  RANGE_OPTION_SIX_MONTHS,
  RANGE_OPTION_NINE_MONTHS,
  RANGE_OPTION_YEAR_TO_DATE,
  RANGE_OPTION_ONE_YEAR,
  RANGE_OPTION_ONE_AND_A_HALF_YEARS,
  RANGE_OPTION_TWO_YEARS,
  RANGE_OPTION_THREE_YEARS,
  RANGE_OPTION_FIVE_YEARS,
  RANGE_OPTION_TEN_YEARS,
  RANGE_OPTION_FIFTEEN_YEARS,
  RANGE_OPTION_TWENTY_YEARS,
  RANGE_OPTION_TWENTY_FIVE_YEARS,
  RANGE_OPTION_FIFTY_YEARS,
  RANGE_OPTION_SEVENTY_FIVE_YEARS,
  RANGE_OPTION_ONE_HUNDRED_YEARS,
  RANGE_OPTION_PREDEFINED_RANGE,
  RANGE_OPTION_SELECT_START_END
];

export function getRangeObjFromRangeTitle(rangeTitle: string): RangeOption {
  return RANGE_OPTIONS_ARRAY.find(r => r.title === rangeTitle) ?? DEFAULT_RANGE_OPTION;
}
